import { useRequest } from 'ahooks';
import React, { useCallback, useState } from 'react';
import { getCountries } from '../../../api/api';
import { Country } from '../../../interfaces';
import { AutoComplete, AutoCompleteItem } from './AutoComplete';

export type CountriesAutoCompleteProps = React.HTMLProps<HTMLInputElement> & {
  defaultValue?: string;
  onCountryChange?: (Country?: Country) => void;
  onDropDownToggle?: (show: boolean) => void;
};

export const CountriesAutoComplete: React.FC<CountriesAutoCompleteProps> = (
  props: CountriesAutoCompleteProps
) => {
  const [filteredItems, setFilteredItems] = useState<AutoCompleteItem[]>([]);
  const [itemSelected, setItemSelected] = useState(false);

  const request = useRequest(getCountries, {
    cacheKey: 'countries-cache',
    staleTime: 60 * 60 * 1000,
  });

  const renderValue = useCallback((item: AutoCompleteItem) => item.value, []);

  const fetchDataCallback = useCallback(
    async (keyword: string) => {
      if (itemSelected) return filteredItems;
      else {
        const r = await request.runAsync();
        const items = r.Data.map((c) => ({ id: c.Id.toString(), value: c.Name }))
          .filter((c) => !keyword || c.value.toLowerCase().startsWith(keyword.toLowerCase()))
          .sort((a, b) => a.value.localeCompare(b.value));
        setFilteredItems(items);
        return items;
      }
    },
    [filteredItems, itemSelected, request]
  );

  const handleSelectionChange = useCallback(
    (item: AutoCompleteItem) => {
      if (props.onCountryChange)
        props.onCountryChange(item ? { Id: parseInt(item.id), Name: item.value } : item);
      setItemSelected(item !== undefined);
    },
    [props]
  );

  return (
    <AutoComplete
      placeholder="Country"
      lazy={false}
      defaultValue={props.defaultValue}
      renderValue={renderValue}
      fetchDataCallback={fetchDataCallback}
      onSelectionChange={handleSelectionChange}
      {...props}
    />
  );
};
